import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BarChart3,
  Calendar,
  GraduationCap,
  Handshake,
  Megaphone,
  MessageCircle,
  QrCode,
  Ticket,
  Users,
  Wallet,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { HERO_CONTACT } from "@/config/heroImages";

const STATS = [
  { value: "12+", label: "Universities" },
  { value: "100+", label: "Societies" },
  { value: "10k+", label: "Students" },
];

const PROS = [
  {
    icon: Wallet,
    title: "First 100 businesses free for 1 year",
    desc: "Join free for your first year — limited to the first 100 businesses",
  },
  {
    icon: GraduationCap,
    title: "Reach the university community",
    desc: "Connect with students and societies across Hong Kong's universities",
  },
  {
    icon: Calendar,
    title: "Create activities students can join",
    desc: "Post events and activities that students can join directly on the platform",
  },
  {
    icon: Megaphone,
    title: "Promote your brand to students — easily",
    desc: "Built-in tools make it easy to get your brand in front of the student community",
  },
];

const REACH = [
  {
    icon: Ticket,
    title: "Publish coupons",
    desc: "Create redeemable coupons students earn with points.",
  },
  {
    icon: Calendar,
    title: "Host events & workshops",
    desc: "List your activities on the student calendar.",
  },
  {
    icon: Handshake,
    title: "Sponsor societies",
    desc: "Fund society events to put your brand in front of engaged members.",
  },
  {
    icon: QrCode,
    title: "Activities & QR Check-in",
    desc: "Run activities with real-time QR check-in and get instant, verified attendance.",
  },
  {
    icon: MessageCircle,
    title: "Chat with Business",
    desc: "Message interested students in real time and keep the conversation going.",
  },
];

const SUPPORT = [
  {
    icon: Users,
    title: "Connect you with the university community",
    desc: "We bring your brand into a community of students and societies across Hong Kong universities",
  },
  {
    icon: Calendar,
    title: "Put your activities in front of students",
    desc: "Your events and activities are listed on the student calendar for easy joining",
  },
  {
    icon: Megaphone,
    title: "Promote your brand to the community",
    desc: "Coupons, events and sponsorships get your brand noticed by students",
  },
  {
    icon: BarChart3,
    title: "Track everything in one dashboard",
    desc: "See activities, redemptions and messages in one partner dashboard",
  },
];

const STEPS = [
  {
    step: "01",
    title: "Apply",
    desc: "Tell us about your business and the students you want to reach.",
  },
  {
    step: "02",
    title: "Set up your offers",
    desc: "Publish coupons, events and activities from your partner dashboard.",
  },
  {
    step: "03",
    title: "Grow your reach",
    desc: "Track redemptions, attendance and messages in one partner dashboard.",
  },
];

const ForBusinessPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <section
        className="relative bg-cover bg-center py-16 md:py-24"
        style={{ backgroundImage: `url(${HERO_CONTACT})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-[#ffa217]/25" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <h1 className="animate-fade-down text-3xl font-extrabold leading-tight text-white md:text-4xl" style={{ animationDelay: "100ms" }}>
            {t("Are you a business or society?")}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl animate-fade-down text-lg leading-relaxed text-white md:text-xl" style={{ animationDelay: "200ms" }}>
            {t("Reach students and societies across the university community in Hong Kong.")}
          </p>
          <p className="mx-auto mt-3 max-w-2xl animate-fade-down leading-relaxed text-gray-200" style={{ animationDelay: "300ms" }}>
            {t("Join our partner network to put your brand in front of thousands of verified students.")}
          </p>
          <div className="mt-9 flex animate-fade-down flex-wrap justify-center gap-3" style={{ animationDelay: "400ms" }}>
            <Link
              to="/business/signup"
              className="inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-8 py-3 font-bold text-white shadow-lg shadow-[#ffa217]/30 transition hover:bg-[#e08f0e]"
            >
              {t("Join as Business")}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-[#ffa217] px-8 py-3 font-bold text-[#ffa217] transition hover:bg-[#fff3e0]"
            >
              {t("Contact Us")}
            </Link>
            <Link
              to="/business/login"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-white/70 px-8 py-3 font-bold text-white transition hover:bg-white/10"
            >
              {t("Business Sign In")}
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-[#ffa217] to-[#ff8c00] py-12">
        <Reveal className="mx-auto grid max-w-5xl grid-cols-3 gap-2.5 px-4 text-center sm:gap-4">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl bg-white/10 px-2 py-5 sm:px-3 sm:py-6"
            >
              <div className="text-2xl font-extrabold text-white sm:text-3xl md:text-4xl">
                {stat.value}
              </div>
              <div className="mt-1 text-xs text-white/80 sm:text-sm md:text-base">
                {t(stat.label)}
              </div>
            </div>
          ))}
        </Reveal>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#333]">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
            <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-[#ffa217]" />
            <h2 className="text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("The pros of partnering with us")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PROS.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-gray-100 p-8 transition hover:-translate-y-1 hover:border-[#ffa217] hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
                  <item.icon size={26} />
                </span>
                <h3 className="mt-5 text-xl font-bold text-[#333]">
                  {t(item.title)}
                </h3>
                <p className="mt-2 leading-relaxed text-gray-600">
                  {t(item.desc)}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-gray-50 py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#333]">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
            <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-[#ffa217]" />
            <h2 className="text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("What you can do to reach students")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {REACH.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-gray-100 p-8 transition hover:-translate-y-1 hover:border-[#ffa217] hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
                  <item.icon size={26} />
                </span>
                <h3 className="mt-5 text-xl font-bold text-[#333]">
                  {t(item.title)}
                </h3>
                <p className="mt-2 leading-relaxed text-gray-600">
                  {t(item.desc)}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#333]">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
            <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-[#ffa217]" />
            <h2 className="text-3xl font-extrabold text-[#333] md:text-4xl">
              {t("What we do to help you reach students & societies")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORT.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-gray-100 p-8 transition hover:-translate-y-1 hover:border-[#ffa217] hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
                  <item.icon size={26} />
                </span>
                <h3 className="mt-5 text-xl font-bold text-[#333]">
                  {t(item.title)}
                </h3>
                <p className="mt-2 leading-relaxed text-gray-600">
                  {t(item.desc)}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-[#1f2937] py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#333]">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
            <div className="text-sm font-bold uppercase tracking-widest text-[#ffa217]">
              {t("How It Works")}
            </div>
            <h2 className="mt-2 text-3xl font-extrabold text-white md:text-4xl">
              {t("Three steps to become a partner")}
            </h2>
          </div>
          <Reveal className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((item) => (
              <div key={item.step} className="rounded-2xl bg-white/5 p-6">
                <div className="text-3xl font-extrabold text-[#ffa217]">
                  {item.step}
                </div>
                <h3 className="mt-3 text-lg font-bold text-white">
                  {t(item.title)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">
                  {t(item.desc)}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fff8f0] py-16 md:py-20">
        <Reveal className="mx-auto max-w-3xl px-4 text-center">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#333]">
            Uninfo<span className="text-[#ffa217]">HK</span>
          </span>
          <h2 className="text-3xl font-extrabold text-[#333] md:text-4xl">
            {t("Ready to grow with Uninfo HK?")}
          </h2>
          <p className="mt-4 leading-relaxed text-gray-600">
            {t("Questions, feedback or partnership ideas? We would love to hear from you.")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-8 py-3 font-bold text-white transition hover:bg-[#e08f0e]"
            >
              {t("Contact Us")}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/business/signup"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-[#ffa217] px-8 py-3 font-bold text-[#ffa217] transition hover:bg-white"
            >
              {t("Join as Business")}
            </Link>
            <Link
              to="/business/login"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-[#333]/30 px-8 py-3 font-bold text-[#333] transition hover:bg-white"
            >
              {t("Business Sign In")}
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default ForBusinessPage;